import { AbstractViewModel } from '../../../utils/viewmodels/viewmodel';
import { useViewModel } from '../../../utils/viewmodels/useViewModel';
import type { ChatMultipleSelectContent } from '../../chatbot.types';
import type { ChatMultipleSelectState } from './chat-multiple-select.state';

export class ChatMultipleSelectViewModel extends AbstractViewModel<ChatMultipleSelectState> {
  static initialState: ChatMultipleSelectState = {
    selected: [],
    isAnswered: false,
  };

  private readonly messageId: string;
  private readonly onConfirm: (messageId: string, optionIds: string[]) => void;

  constructor(
    messageId: string,
    selectedIds: string[] | undefined,
    onConfirm: (messageId: string, optionIds: string[]) => void
  ) {
    super(ChatMultipleSelectViewModel.initialState);
    this.messageId = messageId;
    this.onConfirm = onConfirm;

    this.handleConfirm = this.handleConfirm.bind(this);

    this.update((state) => {
      state.selected = selectedIds ?? [];
      state.isAnswered = selectedIds !== undefined;
    });
  }

  setContent = (content: ChatMultipleSelectContent): void => {
    this.update((state) => {
      state.selected = content.selectedIds ?? state.selected;
      state.isAnswered = content.selectedIds !== undefined;
    });
  };

  isSelected = (optionId: string): boolean => this.state.getValue().selected.includes(optionId);

  handleToggle = (optionId: string): void => {
    if (this.state.getValue().isAnswered) return;
    this.update((state) => {
      state.selected = state.selected.includes(optionId)
        ? state.selected.filter((id) => id !== optionId)
        : [...state.selected, optionId];
    });
  };

  handleConfirm = (): void => {
    this.onConfirm(this.messageId, this.state.getValue().selected);
  };

  isConfirmDisabled = (): boolean => this.state.getValue().selected.length === 0;

  getOptionClassName = (optionId: string): string => {
    const { isAnswered } = this.state.getValue();
    return [
      'chatbot-checkbox-label',
      this.isSelected(optionId) ? 'chatbot-checkbox-label--selected' : '',
      isAnswered ? 'chatbot-checkbox-label--disabled' : '',
    ].join(' ');
  };
}

export const useChatMultipleSelectViewModel = (
  messageId: string,
  content: ChatMultipleSelectContent,
  onConfirm: (messageId: string, optionIds: string[]) => void
) =>
  useViewModel<ChatMultipleSelectState, ChatMultipleSelectViewModel>(
    () => new ChatMultipleSelectViewModel(messageId, content.selectedIds, onConfirm)
  );
